/**
 * Modern Megamenu Component
 * Provides accessible navigation with keyboard support and focus management
 * Enhanced with instant switching between megamenus
 */

class ModernMegamenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('.modern-megamenu__details');
    this.trigger = this.querySelector('.modern-megamenu__trigger');
    this.content = this.querySelector('.modern-megamenu__content');
    this.backdrop = this.querySelector('.modern-megamenu__backdrop');
    
    this.isOpen = false;
    this.closeTimeout = null;
    this.openTimeout = null;
    this.mouseInArea = false; // Track if mouse is in trigger or content area
    
    // Store bound event handler references for cleanup
    this.boundHandlers = {
      handleDetailsToggle: this.handleDetailsToggle.bind(this),
      handleMouseEnter: this.handleMouseEnter.bind(this),
      handleTriggerMouseLeave: this.handleTriggerMouseLeave.bind(this),
      handleContentMouseEnter: this.handleContentMouseEnter.bind(this),
      handleContentMouseLeave: this.handleContentMouseLeave.bind(this),
      handleClick: this.handleClick.bind(this),
      handleDoubleClick: this.handleDoubleClick.bind(this),
      handleClose: this.close.bind(this),
      handleTriggerKeydown: this.handleTriggerKeydown.bind(this),
      handleContentKeydown: this.handleContentKeydown.bind(this),
      handleFocusIn: this.handleFocusIn.bind(this),
      handleResize: this.handleResize.bind(this)
    };
    
    this.init();
  }

  init() {
    if (!this.details || !this.trigger || !this.content) return;
    
    this.setupEventListeners();
    this.setupKeyboardNavigation();
    this.setupAccessibility();
  }

  setupEventListeners() {
    // Prevent default details behavior
    this.details.addEventListener('toggle', this.boundHandlers.handleDetailsToggle);
    
    // Mouse interactions - improved for instant switching
    this.trigger.addEventListener('mouseenter', this.boundHandlers.handleMouseEnter);
    this.trigger.addEventListener('mouseleave', this.boundHandlers.handleTriggerMouseLeave);
    
    this.content.addEventListener('mouseenter', this.boundHandlers.handleContentMouseEnter);
    this.content.addEventListener('mouseleave', this.boundHandlers.handleContentMouseLeave);
    
    // Click interactions - prevent default details behavior
    this.trigger.addEventListener('click', this.boundHandlers.handleClick);
    this.trigger.addEventListener('dblclick', this.boundHandlers.handleDoubleClick);
    this.backdrop?.addEventListener('click', this.boundHandlers.handleClose);
    
    // Keyboard interactions
    this.trigger.addEventListener('keydown', this.boundHandlers.handleTriggerKeydown);
    this.content.addEventListener('keydown', this.boundHandlers.handleContentKeydown);
    
    // Focus management
    document.addEventListener('focusin', this.boundHandlers.handleFocusIn);
    
    // Resize handling
    window.addEventListener('resize', this.boundHandlers.handleResize);
  }

  setupKeyboardNavigation() {
    // Get all focusable elements in the megamenu
    this.focusableElements = this.content.querySelectorAll(
      'a, button, [tabindex]:not([tabindex="-1"])'
    );
  }

  setupAccessibility() {
    // Set initial ARIA states
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-haspopup', 'true');
    
    // Add role if not present
    if (!this.content.getAttribute('role')) {
      this.content.setAttribute('role', 'region');
    }
  }

  handleMouseEnter() {
    this.mouseInArea = true;
    clearTimeout(this.closeTimeout);
    
    // Cancel all pending close timeouts from other megamenus
    const allMegamenus = document.querySelectorAll('modern-megamenu');
    allMegamenus.forEach(megamenu => {
      clearTimeout(megamenu.closeTimeout);
      clearTimeout(megamenu.openTimeout);
    });
    
    // Close other megamenus immediately before opening new one
    this.closeOtherMegamenus();
    
    // Open this megamenu immediately
    this.open();
  }

  handleTriggerMouseLeave() {
    this.mouseInArea = false;
    // Clear any existing close timeout to prevent multiple timers
    clearTimeout(this.closeTimeout);
    // Short delay to allow movement to content area
    this.closeTimeout = setTimeout(() => {
      if (!this.mouseInArea && this.isOpen) {
        this.close();
      }
    }, 200);
  }

  handleContentMouseEnter() {
    this.mouseInArea = true;
    clearTimeout(this.closeTimeout);
  }

  handleContentMouseLeave() {
    this.mouseInArea = false;
    // Clear any existing close timeout to prevent multiple timers
    clearTimeout(this.closeTimeout);
    // Short delay to prevent jarring close
    this.closeTimeout = setTimeout(() => {
      if (!this.mouseInArea && this.isOpen) {
        this.close();
      }
    }, 200);
  }

  handleClick(event) {
    // Check if the click was on the caret icon or wrapper
    const caretIcon = event.target.closest('.modern-megamenu__caret-wrapper');
    
    if (caretIcon) {
      // If clicking on the caret, prevent default and toggle
      event.preventDefault();
      event.stopPropagation();
      
      this.closeOtherMegamenus();
      
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
      return;
    }
    
    // If clicking on the text/summary but not the caret
    event.preventDefault();
    event.stopPropagation();
    
    this.closeOtherMegamenus();
    
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleDoubleClick(event) {
    // Navigate to the link URL on double-click
    const url = this.trigger.getAttribute('data-url');
    if (url) {
      window.location.href = url;
    }
  }

  handleTriggerKeydown(event) {
    switch (event.key) {
      case 'Enter':
        // Handle Enter as navigation if megamenu is closed, or focus first item if open
        if (!this.isOpen) {
          const url = this.trigger.getAttribute('data-url');
          if (url && !event.ctrlKey && !event.shiftKey) {
            window.location.href = url;
            return;
          }
        }
        event.preventDefault();
        this.open();
        this.focusFirstItem();
        break;
      case ' ':
      case 'ArrowDown':
        event.preventDefault();
        this.open();
        this.focusFirstItem();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  handleContentKeydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        this.trigger.focus();
        break;
      case 'Tab':
        this.handleTabNavigation(event);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousItem();
        break;
    }
  }

  handleTabNavigation(event) {
    const firstFocusable = this.focusableElements[0];
    const lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    
    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        this.close();
        this.trigger.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        this.close();
        this.focusNextSibling();
      }
    }
  }

  handleFocusIn(event) {
    // Close megamenu if focus moves outside
    if (!this.contains(event.target) && this.isOpen) {
      this.close();
    }
  }

  handleResize() {
    // Close on resize to prevent layout issues
    if (this.isOpen) {
      this.close();
    }
  }

  closeOtherMegamenus() {
    // Find all other modern-megamenu elements and close them immediately
    const allMegamenus = document.querySelectorAll('modern-megamenu');
    allMegamenus.forEach(megamenu => {
      if (megamenu !== this && megamenu.isOpen) {
        clearTimeout(megamenu.closeTimeout);
        clearTimeout(megamenu.openTimeout);
        
        // Force immediate close without animation to prevent shadow overlap
        megamenu.details.open = false;
        megamenu.trigger.setAttribute('aria-expanded', 'false');
        megamenu.isOpen = false;
        
        // Clear all state classes to reset caret immediately
        megamenu.classList.remove('megamenu-opening', 'megamenu-open', 'megamenu-closing');
      }
    });
  }

  open() {
    if (this.isOpen) return;
    
    // Clear all state classes first
    this.classList.remove('megamenu-opening', 'megamenu-open', 'megamenu-closing');
    
    // Add opening class for instant display
    this.classList.add('megamenu-opening');
    this.classList.add('megamenu-open');
    
    // Set details open first
    this.details.open = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    
    // Screen reader announcement
    this.announceToScreenReader('Modern megamenu opened');
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('megamenu:opened', {
      bubbles: true,
      detail: { megamenu: this }
    }));
  }

  close() {
    if (!this.isOpen) return;
    
    // Clear all state classes and add closing class
    this.classList.remove('megamenu-opening', 'megamenu-open');
    this.classList.add('megamenu-closing');
    
    // Close after animation completes
    setTimeout(() => {
      this.details.open = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.isOpen = false;
      this.classList.remove('megamenu-closing');
      
      // Screen reader announcement
      this.announceToScreenReader('Modern megamenu closed');
      
      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('megamenu:closed', {
        bubbles: true,
        detail: { megamenu: this }
      }));
    }, 200); // Match CSS transition duration
  }

  focusFirstItem() {
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  focusNextItem() {
    const currentIndex = Array.from(this.focusableElements).indexOf(document.activeElement);
    const nextIndex = (currentIndex + 1) % this.focusableElements.length;
    this.focusableElements[nextIndex].focus();
  }

  focusPreviousItem() {
    const currentIndex = Array.from(this.focusableElements).indexOf(document.activeElement);
    const prevIndex = currentIndex === 0 ? this.focusableElements.length - 1 : currentIndex - 1;
    this.focusableElements[prevIndex].focus();
  }

  focusNextSibling() {
    // Focus next header menu item or close
    const nextItem = this.closest('li')?.nextElementSibling?.querySelector('a, button');
    if (nextItem) {
      nextItem.focus();
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  handleDetailsToggle(event) {
    // Update our internal state to match the details state
    this.isOpen = this.details.open;
    
    // Update aria-expanded to match
    this.trigger.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);
    
    // Remove all event listeners to prevent memory leaks
    if (this.details) {
      this.details.removeEventListener('toggle', this.boundHandlers.handleDetailsToggle);
    }
    
    if (this.trigger) {
      this.trigger.removeEventListener('mouseenter', this.boundHandlers.handleMouseEnter);
      this.trigger.removeEventListener('mouseleave', this.boundHandlers.handleTriggerMouseLeave);
      this.trigger.removeEventListener('click', this.boundHandlers.handleClick);
      this.trigger.removeEventListener('keydown', this.boundHandlers.handleTriggerKeydown);
    }
    
    if (this.content) {
      this.content.removeEventListener('mouseenter', this.boundHandlers.handleContentMouseEnter);
      this.content.removeEventListener('mouseleave', this.boundHandlers.handleContentMouseLeave);
      this.content.removeEventListener('keydown', this.boundHandlers.handleContentKeydown);
    }
    
    if (this.backdrop) {
      this.backdrop.removeEventListener('click', this.boundHandlers.handleClose);
    }
    
    // Remove document and window event listeners
    document.removeEventListener('focusin', this.boundHandlers.handleFocusIn);
    window.removeEventListener('resize', this.boundHandlers.handleResize);
    
    // Clear bound handlers reference
    this.boundHandlers = null;
  }
}

// Register the custom element
if (!customElements.get('modern-megamenu')) {
  customElements.define('modern-megamenu', ModernMegamenu);
}

// Auto-close megamenus when clicking outside
document.addEventListener('click', (event) => {
  const megamenus = document.querySelectorAll('modern-megamenu');
  megamenus.forEach(megamenu => {
    if (!megamenu.contains(event.target) && megamenu.isOpen) {
      megamenu.close();
    }
  });
});

// Close all megamenus on escape key
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    const megamenus = document.querySelectorAll('modern-megamenu');
    megamenus.forEach(megamenu => {
      if (megamenu.isOpen) {
        megamenu.close();
      }
    });
  }
});

// Screen reader utility class
if (!document.querySelector('.sr-only-styles')) {
  const srStyles = document.createElement('style');
  srStyles.className = 'sr-only-styles';
  srStyles.textContent = `
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `;
  document.head.appendChild(srStyles);
} 